import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { BookingWidget } from "@/components/BookingWidget";
import { Lightbox } from "@/components/Lightbox";
import { ChevronLeft, Zap, Fuel, Gauge, Users, Settings2, Shield, Star, Loader2, Check, BatteryCharging, Maximize2 } from "lucide-react";
import { api } from "@/lib/api";

const BikeDetail = () => {
  const { id } = useParams();
  const [bike, setBike] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [activeImg, setActiveImg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxIdx, setLightboxIdx] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: b } = await api.get(`/bikes/${id}`);
        if (cancelled) return;
        setBike(b);
        setActiveImg(b.image);
        try {
          const { data: g } = await api.get("/bikes/models/gallery", {
            params: { brand: b.brand, name: b.name },
          });
          if (cancelled) return;
          setGallery(g.images || []);
        } catch (_) {
          setGallery([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin" /></div>;
  if (!bike) return <div className="min-h-screen flex items-center justify-center">Bike not found</div>;

  // Merge hero + gallery, dedupe, keep hero first
  const allImages = [bike.image, ...gallery.filter((u) => u && u !== bike.image)];
  const hasGallery = allImages.length > 1;
  const shownImg = activeImg || bike.image;

  const specs = [
    { icon: Zap, label: "Range", value: bike.range_label || `upto ${bike.range_km} km` },
    { icon: Gauge, label: "Top Speed", value: bike.top_speed_label || `${bike.top_speed} km/h` },
    { icon: BatteryCharging, label: "Charging", value: bike.charging_time || "5 hrs" },
    { icon: Users, label: "Seating", value: bike.seat },
  ];

  const CATEGORY_LABEL = {
    "electric-bicycle": "Electric Bicycle",
    "low-speed-scooter": "Low-Speed Electric Scooter",
    "high-speed-scooter": "High-Speed Electric Scooter",
    "electric-motorcycle": "Electric Motorcycle",
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Navbar />

      <div className="max-w-7xl mx-auto px-5 md:px-8 py-8">
        <Link to="/bikes" data-testid="back-to-bikes" className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-ink mb-6">
          <ChevronLeft size={16} /> Back to all EVs
        </Link>

        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-10">
          <div>
            <button
              type="button"
              onClick={() => setLightboxIdx(allImages.indexOf(shownImg))}
              data-testid="bike-detail-open-lightbox"
              className="group relative aspect-[5/4] w-full rounded-3xl overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200 cursor-zoom-in"
              aria-label="Open image viewer"
            >
              <img
                key={shownImg}
                src={shownImg}
                alt={bike.name}
                data-testid="bike-detail-main-image"
                className="w-full h-full object-cover animate-in fade-in duration-300"
              />
              <div className="absolute top-5 left-5 flex gap-2 pointer-events-none">
                <span className="px-3 py-1.5 rounded-full text-xs font-bold uppercase bg-[#3DDC97] text-[#0A0B10] flex items-center gap-1">
                  <Zap size={12} />Electric
                </span>
                <span className="px-3 py-1.5 rounded-full text-xs font-bold bg-white/95 text-ink backdrop-blur">
                  {CATEGORY_LABEL[bike.category] || bike.category}
                </span>
                <span className="px-3 py-1.5 rounded-full text-xs font-bold bg-black/70 text-white backdrop-blur flex items-center gap-1">
                  <Star size={12} className="fill-[#3DDC97] text-[#3DDC97]" /> {bike.rating} <span className="opacity-60">({bike.reviews})</span>
                </span>
              </div>
              <span className="absolute bottom-4 right-4 w-10 h-10 rounded-full bg-black/60 backdrop-blur text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <Maximize2 size={16} />
              </span>
            </button>

            {hasGallery && (
              <div className="mt-4 flex gap-3 overflow-x-auto pb-1" data-testid="bike-detail-thumbs">
                {allImages.map((url, i) => {
                  const active = url === shownImg;
                  return (
                    <button
                      key={`${url}-${i}`}
                      type="button"
                      onClick={() => setActiveImg(url)}
                      data-testid={`thumb-${i}`}
                      className={`shrink-0 w-20 h-20 rounded-2xl overflow-hidden border-2 transition-all ${active ? "border-[#3DDC97] shadow-md scale-[1.03]" : "border-transparent hover:border-gray-300 opacity-70 hover:opacity-100"}`}
                      aria-label={`Show image ${i + 1}`}
                    >
                      <img src={url} alt="" className="w-full h-full object-cover" />
                    </button>
                  );
                })}
              </div>
            )}

            <div className="mt-8">
              <p className="text-[11px] uppercase tracking-widest font-bold text-gray-500">{bike.brand}</p>
              <h1 className="font-display font-extrabold text-4xl md:text-5xl mt-1 tracking-tight">{bike.name}</h1>
              <p className="text-gray-600 mt-4 max-w-2xl leading-relaxed">{bike.description}</p>
            </div>

            {/* Specs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8" data-testid="bike-specs">
              {specs.map((s, i) => (
                <div key={i} className="card-premium p-5">
                  <s.icon className="text-[#2D7FF9]" size={18} />
                  <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mt-3">{s.label}</p>
                  <p className="font-display font-bold text-lg mt-1 font-mono-num">{s.value}</p>
                </div>
              ))}
            </div>

            {/* Features */}
            <div className="mt-10">
              <h3 className="font-display font-bold text-xl">What's included</h3>
              <div className="grid sm:grid-cols-2 gap-3 mt-5">
                {bike.features?.map((f, i) => (
                  <div key={i} className="flex items-center gap-3 p-4 bg-white border border-gray-100 rounded-xl">
                    <Check size={16} className="text-[#3DDC97]" />
                    <span className="text-sm font-medium">{f}</span>
                  </div>
                ))}
                <div className="flex items-center gap-3 p-4 bg-white border border-gray-100 rounded-xl">
                  <Shield size={16} className="text-[#2D7FF9]" />
                  <span className="text-sm font-medium">Full insurance</span>
                </div>
                <div className="flex items-center gap-3 p-4 bg-white border border-gray-100 rounded-xl">
                  <Check size={16} className="text-[#3DDC97]" />
                  <span className="text-sm font-medium">24×7 roadside support</span>
                </div>
              </div>
            </div>

            {/* Available Plans */}
            <div className="mt-10">
              <h3 className="font-display font-bold text-xl">Available Plans</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
                {[
                  { l: "Hourly", desc: "Quick errands" },
                  { l: "Daily", desc: "Full day rides", hl: true },
                  { l: "Weekly", desc: "Week long stay" },
                  { l: "Monthly", desc: "Long-term use" },
                ].map((p, i) => (
                  <div key={i} className={`p-5 rounded-2xl border ${p.hl ? "border-[#3DDC97] bg-[#3DDC97]/5" : "border-gray-200 bg-white"}`}>
                    <p className="text-[10px] uppercase tracking-widest font-bold text-gray-500">{p.l}</p>
                    <p className="font-display font-bold text-lg mt-1">{p.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div>
            <BookingWidget bike={bike} />
          </div>
        </div>
      </div>

      <Footer />

      {lightboxIdx !== null && (
        <Lightbox
          images={allImages}
          index={Math.max(0, lightboxIdx)}
          alt={`${bike.brand} ${bike.name}`}
          onIndexChange={(i) => {
            setLightboxIdx(i);
            setActiveImg(allImages[i]);
          }}
          onClose={() => setLightboxIdx(null)}
        />
      )}
    </div>
  );
};

export default BikeDetail;
